import React from 'react';
import Button from '../../components/Form/Button';
import { Invoice, InvoiceHire } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { buildDeductionTotals } from '../InvoiceHire/invoiceHireListHelpers';
import InvoiceHireHistoryRows from './InvoiceHireHistoryRows';

interface ClientHistoryTableProps {
  invoices: Invoice[];
  invoiceHires: InvoiceHire[];
  deductions?: any[];
  getClientName: (clientId: string) => string;
  getVesselName: (vesselId: string) => string;
  onEdit: (invoice: Invoice) => void;
  onDelete: (invoice: Invoice) => void;
  onGenerateLetter: (invoice: Invoice, language: 'en' | 'ar') => void;
}

const HEADERS = ['Client', 'Vessel', 'Description', 'Total', 'Deductions', 'Net Remaining', 'Status', 'Debit Notes', 'Actions'];

/** Client History table: legacy invoices + all invoice-hire rows */
const ClientHistoryTable: React.FC<ClientHistoryTableProps> = ({
  invoices, invoiceHires, deductions, getClientName, getVesselName, onEdit, onDelete, onGenerateLetter,
}) => (
  <div className="card">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {HEADERS.map((h) => (
              <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {invoices.map((invoice) => {
            const debitNotesCount = Array.isArray(invoice.debitNotes) ? invoice.debitNotes.length : (invoice.debitNotes as any)?.count || 0;
            const hasDebitNotes = debitNotesCount > 0;
            const sym = resolveCurrencyCode(invoice.currency) === 'USD' ? '$' : 'EGP';
            const statusCls = invoice.status === 'paid' ? 'bg-green-100 text-green-800' : invoice.status === 'overdue' ? 'bg-red-100 text-red-800' : invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' : invoice.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800';
            return (
              <tr key={invoice.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getClientName(invoice.clientId)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getVesselName(invoice.vesselId)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.description}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sym} {invoice.total.toLocaleString()}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sym} {invoice.netRemaining.toLocaleString()}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>{invoice.status.replace('_', ' ')}</span></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${hasDebitNotes ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'}`}>{debitNotesCount} {debitNotesCount === 1 ? 'Note' : 'Notes'}</span>
                    {hasDebitNotes && (
                      <div className="flex space-x-1">
                        <button onClick={() => onGenerateLetter(invoice, 'en')} className="text-xs bg-primary-600 text-white px-2 py-1 rounded hover:bg-primary-700" title="Generate English Letter">EN</button>
                        <button onClick={() => onGenerateLetter(invoice, 'ar')} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700" title="Generate Arabic Letter">AR</button>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <Button onClick={() => onEdit(invoice)} variant="secondary" size="sm">Edit</Button>
                  <Button onClick={() => onDelete(invoice)} variant="danger" size="sm">Delete</Button>
                </td>
              </tr>
            );
          })}
          <InvoiceHireHistoryRows
            invoices={invoiceHires}
            deductionTotals={buildDeductionTotals(deductions || [])}
            getClientName={getClientName}
            getVesselName={getVesselName}
          />
        </tbody>
      </table>
      {invoices.length === 0 && invoiceHires.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No invoices found</p>
        </div>
      )}
    </div>
  </div>
);

export default ClientHistoryTable;
